import React, { useState, useEffect } from 'react';
import { Terminal, CheckCircle2, Copy } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { udfApi } from '../../../api/udfApi';

const FunctionPreview = ({ function: udf }) => {
    const [copied, setCopied] = useState(false);
    const [fullUdf, setFullUdf] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const isDark = document.documentElement.classList.contains('dark');

    useEffect(() => {
        if (!udf) {
            setFullUdf(null);
            return;
        }

        const fetchFullUdf = async () => {
            setIsLoading(true);
            try {
                const data = await udfApi.getUDF(udf.id);
                setFullUdf(data);
            } catch (err) {
                console.error('Failed to fetch UDF details:', err);
                // Fallback to the basic udf data if fetch fails
                setFullUdf(udf);
            } finally {
                setIsLoading(false);
            }
        };

        fetchFullUdf();
    }, [udf]);

    // Use fullUdf if available, otherwise fall back to udf
    const displayUdf = fullUdf || udf;

    if (!udf) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center">
                <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg mb-3">
                    <Terminal size={24} className="text-gray-300 dark:text-gray-700" />
                </div>
                <h3 className="text-xs font-medium text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-1">Select a function</h3>
                <p className="text-[10px] text-gray-400 dark:text-gray-600 text-center max-w-xs">
                    Choose a function from the sidebar to view details
                </p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-4 h-4 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Loading...</span>
                </div>
            </div>
        );
    }

    const handleCopy = () => {
        navigator.clipboard.writeText(displayUdf.definition || '');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="h-full flex flex-col bg-white dark:bg-gray-950 transition-colors">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                            {displayUdf.name}
                        </h2>
                        <span className="text-[10px] font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider shrink-0">
                            {displayUdf.language || 'SQL'} • {displayUdf.source}
                        </span>
                    </div>
                    <code className="text-[11px] font-mono text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50 px-2.5 py-1 rounded-md block">
                        {displayUdf.signature}
                    </code>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                <div className="grid grid-cols-2 gap-4">
                    {/* Description */}
                    <div className="flex flex-col">
                        <h4 className="text-[10px] font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-3">Description</h4>
                        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                            {displayUdf.description || "No description provided for this function."}
                        </p>
                    </div>

                    {/* SQL Definition */}
                    <div className="flex flex-col border-l border-gray-100 dark:border-gray-800 pl-4">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-[10px] font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">SQL Definition</h4>
                            <button
                                onClick={handleCopy}
                                className="p-1.5 text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-all rounded-md"
                                title="Copy code"
                            >
                                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                            </button>
                        </div>
                        <div className="rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800">
                            {displayUdf.definition ? (
                                <SyntaxHighlighter
                                    language="sql"
                                    style={isDark ? vscDarkPlus : prism}
                                    customStyle={{
                                        margin: 0,
                                        padding: '1rem',
                                        fontSize: '11px',
                                        lineHeight: '1.6',
                                        background: isDark ? '#111827' : '#f9fafb',
                                    }}
                                    codeTagProps={{
                                        style: {
                                            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                                        }
                                    }}
                                >
                                    {displayUdf.definition}
                                </SyntaxHighlighter>
                            ) : (
                                <div className="p-4 text-gray-400 dark:text-gray-600 font-mono text-[11px]">
                                    -- No definition available
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FunctionPreview;
